import { Clickable } from '@carvana/merch-components';
import { useEffectOnce, useSubsequentEffect } from '@carvana/merch-react-hooks';
import { JSX, useContext, useState } from 'react';
import HideOnNative from '@/common/HideOnNative';
import TrackWrapper from '@/common/TrackWrapper';
import LinksLoadingCard, {
  LinkCardType
} from '@/home/HeroCardsReturnUser/LinkCards/LinksLoadingCard';
import formatEvent from '@/home/utils/formatEvent';
import Sections from '~/constants/home/sections';
import RecentSearchesContext from '~/context/home/RecentSearchesContext';
import { SavedSearch } from '~/contracts/common';
import { RecentSearch } from '~/contracts/home';
import { nativeSrpPayload } from '~/native/home/utils';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import RecentSearchLink from './RecentSearchLink';
import SavedSearchLink from './SavedSearchLink';
import SuggestedLinks from './SuggestedLinks';
import useGetSRPLinksToShow, { SRPLinkTitles } from './useGetSRPLinksToShow';
import { SuggestedSearch } from './useGetSuggestedSearches';
import {
  Header,
  HeaderWrapper,
  LinkCardContainer,
  LinksContainer,
  linkStyles
} from '../LinkCard.styles';

function SRPLinks() {
  const {
    header,
    searches,
    showLoading,
    showLink = false
  } = useGetSRPLinksToShow();
  const {
    recentSearches: { data: recentSearches }
  } = useContext(RecentSearchesContext);
  const [linksToShow, setLinksToShow] = useState<
    SavedSearch[] | RecentSearch[] | SuggestedSearch[]
  >([]);
  const [hasLink, setHasLink] = useState(false);
  const [headerToShow, setHeaderToShow] = useState<SRPLinkTitles>(
    SRPLinkTitles.None
  );
  const [isLoaded, setIsLoaded] = useState(false);

  //So that we don't swap searches when a user saves a search
  useEffectOnce(
    () => {
      setLinksToShow(searches);
      setHasLink(showLink);
      setHeaderToShow(header);
      setIsLoaded(true);
    },
    [header, searches, showLink],
    !showLoading
  );

  function getCustomAttributes() {
    let type: string;
    switch (headerToShow) {
      case SRPLinkTitles.Saved:
        type = 'saved';
        break;
      case SRPLinkTitles.Recent:
        type = 'recent';
        break;
      case SRPLinkTitles.Suggested:
        type = 'suggested';
        break;
      default:
        type = 'suggested';
        break;
    }

    return {
      description: 'Search Card',
      type
    };
  }

  // Update recent searches when they are added to saved searches without swapping card to saved searches
  useSubsequentEffect(
    () => {
      if (headerToShow === SRPLinkTitles.Recent) {
        setLinksToShow(recentSearches.slice(0, 6));
      }
    },
    [recentSearches, headerToShow],
    isLoaded
  );

  function LinkCardToUse(): JSX.Element | null {
    switch (headerToShow) {
      case SRPLinkTitles.Saved:
        return <SavedSearchLink links={linksToShow as SavedSearch[]} />;
      case SRPLinkTitles.Recent:
        return <RecentSearchLink links={linksToShow as RecentSearch[]} />;
      case SRPLinkTitles.Suggested:
        return <SuggestedLinks links={linksToShow as SuggestedSearch[]} />;
      default:
        return null;
    }
  }

  return isLoaded ? (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', getCustomAttributes())}
      trackOnce
    >
      <LinkCardContainer data-qa="srp-link-card">
        <HeaderWrapper>
          <Header data-qa="srp-link-card-header">{headerToShow}</Header>
          {hasLink && (
            <HideOnNative>
              <Clickable
                element="a"
                data-native-payload={nativeSrpPayload}
                data-analytics-id="Home feed card"
                data-analytics-attributes={JSON.stringify({
                  description: 'Search Card',
                  type: 'saved',
                  item: 'view all'
                })}
                className={linkStyles}
                href="/cars?merch_manage_alerts=true"
                data-qa="manage-saved-searches-link"
              >
                View all
              </Clickable>
            </HideOnNative>
          )}
        </HeaderWrapper>
        <LinksContainer>
          <LinkCardToUse />
        </LinksContainer>
      </LinkCardContainer>
    </TrackWrapper>
  ) : (
    <LinksLoadingCard card={LinkCardType.SRP} />
  );
}

export default withErrorBoundary(
  SRPLinks,
  undefined,
  undefined,
  Sections.SRPLinks
);
